import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  TestState, 
  Answer, 
  CategoryResult, 
  TestResults,
  UserDetails
} from '../types';
import { testData } from '../data/testData';
import { saveUserToFirebase, saveTestResultsToFirebase } from '../services/firebaseService';

const calculateCategoryScore = (
  categoryId: string, 
  answers: Record<string, Answer>
): CategoryResult => {
  const category = testData.find(cat => cat.id === categoryId);
  if (!category) {
    throw new Error(`Category with ID ${categoryId} not found`);
  }
  
  let totalScore = 0;
  const totalPossible = 100;
  
  category.questions.forEach(question => {
    const answer = answers[question.id];
    if (answer) {
      totalScore += (answer.value / 5) * (100 / category.questions.length);
    }
  });
  
  const percentage = (totalScore / totalPossible) * 100;
  
  let status: 'Ready' | 'Almost Ready' | 'Not Ready';
  if (percentage >= 80) status = 'Ready';
  else if (percentage >= 60) status = 'Almost Ready';
  else status = 'Not Ready';
  
  return {
    categoryId,
    score: Math.round(totalScore),
    totalPossible,
    percentage: Math.round(percentage),
    status
  };
};

const calculateResults = (answers: Record<string, Answer>): TestResults => {
  const categoryResults = testData.map(category => 
    calculateCategoryScore(category.id, answers)
  );
  
  const overallScore = categoryResults.reduce((sum, result) => sum + result.score, 0);
  const overallTotalPossible = categoryResults.reduce((sum, result) => sum + result.totalPossible, 0);
  const overallPercentage = overallTotalPossible > 0 
    ? (overallScore / overallTotalPossible) * 100 
    : 0;
  
  let overallStatus: 'Ready' | 'Almost Ready' | 'Not Ready';
  if (overallPercentage >= 80) overallStatus = 'Ready';
  else if (overallPercentage >= 60) overallStatus = 'Almost Ready';
  else overallStatus = 'Not Ready';
  
  return {
    categories: categoryResults,
    overallScore,
    overallPercentage,
    overallStatus,
    completedAt: new Date().toISOString()
  };
};

export const useTestStore = create<TestState>()(
  persist(
    (set, get) => ({
      categories: testData,
      currentCategoryIndex: 0,
      answers: {},
      timeRemaining: 60 * 60,
      isTestStarted: false,
      isTestCompleted: false,
      results: null,
      userDetails: null,
      hasCompletedOnboarding: false,
      
      // Actions
      startTest: () => set({ 
        isTestStarted: true, 
        currentCategoryIndex: 0,
        timeRemaining: 60 * 60,
        isTestCompleted: false,
        results: null
      }),
      
      resetTest: () => set({ 
        answers: {},
        currentCategoryIndex: 0,
        timeRemaining: 60 * 60,
        isTestStarted: false,
        isTestCompleted: false,
        results: null,
        hasCompletedOnboarding: false,
        userDetails: null
      }),
      
      setAnswer: (questionId: string, value: number) => set(state => ({
        answers: {
          ...state.answers,
          [questionId]: { questionId, value }
        }
      })),
      
      nextCategory: () => set(state => ({
        currentCategoryIndex: Math.min(state.currentCategoryIndex + 1, state.categories.length - 1)
      })),
      
      previousCategory: () => set(state => ({
        currentCategoryIndex: Math.max(state.currentCategoryIndex - 1, 0)
      })),
      
      goToCategory: (index: number) => set({
        currentCategoryIndex: index
      }),
      
      decrementTimer: () => set(state => ({
        timeRemaining: Math.max(state.timeRemaining - 1, 0)
      })),
      
      completeTest: async () => {
        const state = get();
        const results = calculateResults(state.answers);
        
        set({
          isTestCompleted: true,
          isTestStarted: false,
          results
        });

        if (state.userDetails) {
          try {
            const userId = await saveUserToFirebase(state.userDetails);
            await saveTestResultsToFirebase(userId, results);
          } catch (error) {
            console.error('Firebase save error:', error);
          }
        }
      },

      setUserDetails: async (details: UserDetails) => {
        try {
          // Save to Firebase and wait for the result
          const userId = await saveUserToFirebase(details);
          
          // Update local state after successful save
          set({ 
            userDetails: details,
            hasCompletedOnboarding: true
          });
          
          return userId; // Return the user ID for future reference
        } catch (error) {
          console.error('Error setting user details:', error);
          throw error; // Propagate the error to be handled by the form
        }
      }
    }),
    {
      name: 'export-readiness-test',
      partialize: (state) => ({
        answers: state.answers,
        currentCategoryIndex: state.currentCategoryIndex,
        isTestStarted: state.isTestStarted,
        isTestCompleted: state.isTestCompleted,
        results: state.results,
        userDetails: state.userDetails,
        hasCompletedOnboarding: state.hasCompletedOnboarding
      })
    }
  )
);